import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { PortalShell } from "../components/PortalShell";
import { BrokerOnly } from "../components/BrokerOnly";
import { brokerApi } from "../api/broker";
import type { OnboardingCourseDto, OnboardingProgressDto } from "../api/types";

// External Intake & Collection v1.0 PR 9b §8.4 — sequential onboarding
// course player. Items unlock in OnboardingItem.Sequence order via the
// PR 9a service (AttestItemAsync rejects out-of-order items + locks
// once the broker is Certified). The UI mirrors that order and surfaces
// the per-item attestation button only on the next-due item.

export function BrokerCoursePage() {
  return (
    <BrokerOnly>
      <CourseBody />
    </BrokerOnly>
  );
}

function CourseBody() {
  const [course, setCourse] = useState<OnboardingCourseDto | null>(null);
  const [progress, setProgress] = useState<OnboardingProgressDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const attest = useSubmittingMutation(
    async (itemId: string) => {
      const next = await brokerApi.attestItem(itemId);
      setProgress(next);
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Couldn't record your attestation."),
    },
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [c, p] = await Promise.all([brokerApi.getCourse(), brokerApi.getProgress()]);
        if (cancelled) return;
        setCourse(c ?? null);
        setProgress(p);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Couldn't load the course.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleAttest(itemId: string) {
    setError(null);
    void attest.submit(itemId);
  }

  if (error) {
    return (
      <PortalShell pageTitle="Onboarding course">
        <p className="text-small text-status-error" role="alert">{error}</p>
      </PortalShell>
    );
  }
  if (!course || !progress) {
    return (
      <PortalShell pageTitle="Onboarding course">
        <p className="text-small text-gray-mid">Loading…</p>
      </PortalShell>
    );
  }

  const sortedItems = [...course.items].sort((a, b) => a.sequence - b.sequence);
  const completed = new Set<string>();
  if (progress.nextItemId) {
    for (const it of sortedItems) {
      if (it.id === progress.nextItemId) break;
      completed.add(it.id);
    }
  } else {
    // No next item → either course not started (completed=0) or all done.
    if (progress.completedItems >= progress.totalItems) {
      for (const it of sortedItems) completed.add(it.id);
    }
  }

  return (
    <PortalShell pageTitle="Onboarding course">
      <h1 className="text-h2 font-display text-onyx m-0">{course.title}</h1>
      {course.description && <p className="text-small text-gray-mid mt-space-2">{course.description}</p>}

      <p className="text-small text-onyx mt-space-3" data-testid="course-progress">
        {progress.completedItems} of {progress.totalItems} complete
        {progress.brokerCertified && " — you're certified!"}
      </p>

      {progress.brokerCertified && (
        <div className="mt-space-3 p-space-3 border border-gold/40 rounded-md bg-gold/5">
          <p className="text-small text-onyx m-0">Onboarding complete.</p>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            <Link to="/portal/broker/deals/new" className="underline">Submit your first deal</Link>
            {" · "}
            <Link to="/portal/broker/materials" className="underline">Open the materials library</Link>
          </p>
        </div>
      )}

      <ol className="mt-space-5 flex flex-col gap-space-3 list-none p-0">
        {sortedItems.map((item) => {
          const isDone = completed.has(item.id);
          const isCurrent = progress.nextItemId === item.id;
          const isLocked = !isDone && !isCurrent;
          return (
            <li
              key={item.id}
              data-testid={`course-item-${item.sequence}`}
              data-state={isDone ? "done" : isCurrent ? "current" : "locked"}
              className={`p-space-4 rounded-md border ${
                isCurrent
                  ? "border-gold bg-gold/10"
                  : isDone
                    ? "border-border-light bg-white-pure"
                    : "border-border-light bg-white-warm/40 opacity-70"
              }`}
            >
              <div className="flex items-center justify-between gap-space-3">
                <div>
                  <p className="text-small font-medium text-onyx m-0">
                    {item.sequence}. {item.title}
                  </p>
                  {item.description && (
                    <p className="text-caption text-gray-mid m-0 mt-space-1">{item.description}</p>
                  )}
                  <p className="text-caption text-gray-mid m-0 mt-space-1">
                    {item.kind === "Video" ? "Video" : "Document"}
                    {item.minimumDurationSeconds ? ` · ~${Math.round(item.minimumDurationSeconds / 60)} min` : ""}
                  </p>
                </div>
                <div className="shrink-0">
                  {isDone && (
                    <span className="text-caption text-status-success" aria-label="Completed">Completed</span>
                  )}
                  {isLocked && (
                    <span className="text-caption text-gray-mid" aria-label="Locked">Locked</span>
                  )}
                  {isCurrent && (
                    <div className="flex flex-col items-end gap-space-1">
                      <SubmitButton
                        onClick={() => handleAttest(item.id)}
                        pending={attest.pending}
                        pendingLabel="Recording…"
                        data-testid={`course-attest-${item.sequence}`}
                      >
                        I have reviewed this
                      </SubmitButton>
                      <SubmittingHint pending={attest.pending} />
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <p className="text-caption text-gray-mid mt-space-5">
        <Link to="/portal/broker" className="underline">Back to dashboard</Link>
      </p>
    </PortalShell>
  );
}
